import { useEffect, useState } from 'react'
import { ZapIcon, DollarSign, Users, Activity, Clock, Gauge, Coins, Wallet, ShoppingBag } from 'lucide-react'
import { PageHeader } from '@/components/common/page-header'
import { api } from '@/lib/axios'
import { useAuthStore } from '@/store/auth'
import { HTTP_STATUS_CODE } from '@/constants/http'
import { toast } from '@/hooks/use-toast'
import { useLoader } from '@/contexts/LoaderContext'
import MetricCard from '@/components/dashboard/metric-card'
import { DatePicker } from '@/components/ui/date-picker'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { calculateTotalsByDate } from '@/utils/dashboard-dates-util'
import { calculateOverallTotals } from '@/utils/dashboard-dates-util'
import { estimateCost } from '@/utils/dashboard-dates-util'
import { formatNumber } from '@/utils/dashboard-dates-util'
import RequestsChart from '@/components/dashboard/requests-chart'
import UsageTable from '@/components/dashboard/usage-table'
import PurchasesChart from '@/components/dashboard/purchases-chart'

const loc = '@/pages/Dashboard'

/** Payload retornado pelo endpoint `usage-overview`. */
interface UsageOverview {
  periodStart: string
  periodEnd: string
  activeUsers: number
  totalCustomers: number
  usageLevelPct: number
  interactions: {
    messages: number
    examConsultants: number
    signsSymptomsAnalyses: number
    examAnalyses: number
    total: number
  }
  estimatedActiveMinutes: number
  credits: { consumedInMonth: number; purchasedInMonth: number; availableNow: number }
}

/** Período pré-definido do filtro do dashboard. */
type DashboardPeriod = 'TODAY' | 'THIS_WEEK' | 'LAST_WEEK' | 'THIS_MONTH' | 'LAST_3_MONTHS' | 'LAST_6_MONTHS' | 'LAST_12_MONTHS' | 'CUSTOM'

const PERIOD_LABELS: Record<DashboardPeriod, string> = {
  TODAY: 'Hoje',
  THIS_WEEK: 'Essa semana',
  LAST_WEEK: 'Semana passada',
  THIS_MONTH: 'Esse mês',
  LAST_3_MONTHS: 'Últimos 3 meses',
  LAST_6_MONTHS: 'Últimos 6 meses',
  LAST_12_MONTHS: 'Último ano',
  CUSTOM: 'Personalizado'
}

const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000

function formatDuration(totalMinutes: number): string {
  if (!totalMinutes || totalMinutes < 1) return '0min'
  const hours = Math.floor(totalMinutes / 60)
  const mins = totalMinutes % 60
  if (hours === 0) return `${mins}min`
  if (mins === 0) return `${hours}h`
  return `${hours}h ${mins}min`
}

function formatBRL(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0)
}

export default function Dashboard() {
  const { user } = useAuthStore()
  const { showLoader, hideLoader, isLoading } = useLoader()
  const [data, setData] = useState([])
  const [purchases, setPurchases] = useState([])
  const [overview, setOverview] = useState<UsageOverview | null>(null)
  /** Filtro de período. Default = HOJE. */
  const [overviewPeriod, setOverviewPeriod] = useState<DashboardPeriod>('TODAY')
  // Datas só para CUSTOM (DatePicker em ISO yyyy-MM-dd).
  const [overviewStart, setOverviewStart] = useState<string>('')
  const [overviewEnd, setOverviewEnd] = useState<string>('')
  const usageByDate = calculateTotalsByDate(data)
  const { totalRequests, totalInputTokens, totalOutputTokens } = calculateOverallTotals(data)

  // Mantemos o custo estimado pois é informação financeira útil — o cálculo
  // internamente usa tokens, mas o card só exibe R$ ao usuário.
  const estimatedCost = estimateCost(totalInputTokens, totalOutputTokens)

  const chronologicalData = [...usageByDate].reverse()

  const _fetchData = async (refetch: boolean) => {
    try {
      if (!refetch && data.length) return

      showLoader()

      const response = await api.get('/', {
        params: {
          action: 'dashboard',
          userRef: user?.ref,
          userRole: user?.role
        }
      })

      if (response.data.statusCode == HTTP_STATUS_CODE.OK) {
        setData(response.data.body.filter((x: any) => x.results.length > 0))
      } else {
        toast({
          variant: 'destructive',
          title: 'Ops ...',
          description: response?.data?.statusMessage || 'Erro ao buscar dados!'
        })
      }
    } catch (err) {
      console.error(`Unhandled error at ${loc}._fetchData. Details: ${err}`)
    } finally {
      hideLoader()
    }
  }

  const _fetchPurchases = async (refetch: boolean) => {
    try {
      if (!refetch && data.length) return

      showLoader()

      const response = await api.get('/', {
        params: {
          action: 'purchases',
          userRef: user?.ref,
          userRole: user?.role
        }
      })

      if (response.data.statusCode == HTTP_STATUS_CODE.OK) {
        setPurchases(response.data.body?.reverse())
      } else {
        toast({
          variant: 'destructive',
          title: 'Ops ...',
          description: response?.data?.statusMessage || 'Erro ao buscar dados!'
        })
      }
    } catch (err) {
      console.error(`Unhandled error at ${loc}._fetchPurchases. Details: ${err}`)
    } finally {
      hideLoader()
    }
  }

  const _fetchOverview = async (refetch: boolean, args?: { period?: DashboardPeriod; start?: string; end?: string }) => {
    try {
      if (!refetch && overview) return
      const period = args?.period ?? overviewPeriod
      showLoader()
      const params: Record<string, string | undefined> = {
        action: 'usage-overview',
        userRef: user?.ref,
        userRole: user?.role,
        period
      }
      if (period === 'CUSTOM') {
        params.startDate = args?.start ? new Date(args.start + 'T00:00:00').toISOString() : undefined
        params.endDate = args?.end ? new Date(args.end + 'T23:59:59').toISOString() : undefined
      }
      const response = await api.get('/', { params })
      if (response.data.statusCode == HTTP_STATUS_CODE.OK) {
        setOverview(response.data.body?.[0] || null)
      } else {
        toast({
          variant: 'destructive',
          title: 'Ops ...',
          description: response?.data?.statusMessage || 'Erro ao buscar visão geral!'
        })
      }
    } catch (err) {
      console.error(`Unhandled error at ${loc}._fetchOverview. Details: ${err}`)
    } finally {
      hideLoader()
    }
  }

  /** Aplica o período selecionado. Para CUSTOM, valida client-side antes de chamar a API. */
  const _applyOverviewPeriod = () => {
    if (overviewPeriod !== 'CUSTOM') {
      _fetchOverview(true, { period: overviewPeriod })
      return
    }
    if (!overviewStart || !overviewEnd) {
      toast({ variant: 'destructive', title: 'Atenção', description: 'Informe data inicial e final.' })
      return
    }
    if (overviewStart > overviewEnd) {
      toast({ variant: 'destructive', title: 'Atenção', description: 'A data inicial deve ser anterior à final.' })
      return
    }
    const startMs = new Date(overviewStart + 'T00:00:00').getTime()
    const endMs = new Date(overviewEnd + 'T23:59:59').getTime()
    if (endMs - startMs > ONE_YEAR_MS) {
      toast({ variant: 'destructive', title: 'Atenção', description: 'O range personalizado é limitado a 1 ano.' })
      return
    }
    _fetchOverview(true, { period: 'CUSTOM', start: overviewStart, end: overviewEnd })
  }

  /** Quando muda a opção do select, refaz a busca automaticamente (exceto CUSTOM, que espera Aplicar). */
  const _handlePeriodChange = (next: DashboardPeriod) => {
    setOverviewPeriod(next)
    if (next !== 'CUSTOM') _fetchOverview(true, { period: next })
  }

  useEffect(() => {
    _fetchData(false)
    _fetchPurchases(false)
    _fetchOverview(false, { period: 'TODAY' })
  }, [])

  return (
    <>
      <div className="pb-4">
        <PageHeader title="Dashboard Overview" />
      </div>

      {/* Visão geral consolidada do mês — uso da plataforma pelos usuários */}
      <section className="mb-6">
        <div className="flex items-end justify-between gap-3 mb-3 smAndDown:flex-col smAndDown:items-stretch">
          <div>
            <h2 className="font-display text-lg font-semibold text-foreground">Visão geral</h2>
            <p className="text-sm text-muted-foreground">
              {overview
                ? `Período: ${new Date(overview.periodStart).toLocaleDateString('pt-BR')} a ${new Date(overview.periodEnd).toLocaleDateString('pt-BR')}`
                : 'Consolidado de utilização da plataforma'}
            </p>
          </div>
          <div className="flex items-end gap-2 smAndDown:flex-wrap smAndDown:w-full mdAndDown:flex-wrap">
            <div className="flex flex-col gap-1 min-w-[180px] smAndDown:w-full smAndDown:min-w-0 mdAndDown:flex-1 mdAndDown:min-w-[200px]">
              <label htmlFor="overview-period" className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Período</label>
              <Select value={overviewPeriod} onValueChange={v => _handlePeriodChange(v as DashboardPeriod)}>
                <SelectTrigger id="overview-period" className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(PERIOD_LABELS) as DashboardPeriod[]).map(p => (
                    <SelectItem key={p} value={p}>
                      {PERIOD_LABELS[p]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {overviewPeriod === 'CUSTOM' && (
              <>
                <div className="flex flex-col gap-1 min-w-[160px] smAndDown:flex-1 mdAndDown:flex-1">
                  <label htmlFor="overview-start" className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">De</label>
                  <DatePicker
                    id="overview-start"
                    size="sm"
                    value={overviewStart}
                    maxDate={overviewEnd || undefined}
                    onChange={v => setOverviewStart(v || '')}
                    placeholder="dd/mm/aaaa"
                    clearable={false}
                  />
                </div>
                <div className="flex flex-col gap-1 min-w-[160px] smAndDown:flex-1 mdAndDown:flex-1">
                  <label htmlFor="overview-end" className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Até</label>
                  <DatePicker
                    id="overview-end"
                    size="sm"
                    value={overviewEnd}
                    minDate={overviewStart || undefined}
                    onChange={v => setOverviewEnd(v || '')}
                    placeholder="dd/mm/aaaa"
                    clearable={false}
                  />
                </div>
                <button
                  type="button"
                  onClick={_applyOverviewPeriod}
                  className="h-9 px-4 bg-primary text-primary-foreground rounded-md text-sm font-semibold hover:brightness-110 transition smAndDown:w-full"
                >
                  Aplicar
                </button>
              </>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 smAndUp:grid-cols-2 mdAndUp:grid-cols-3 lgAndUp:grid-cols-4 gap-4">
          <MetricCard
            title="Usuários ativos"
            value={overview ? `${overview.activeUsers}/${overview.totalCustomers}` : '—'}
            description="Usaram a plataforma no período"
            icon={<Users className="h-6 w-6" />}
            color="primary"
            isLoading={isLoading}
          />
          <MetricCard
            title="Interações realizadas"
            value={overview ? formatNumber(overview.interactions.total) : '—'}
            description={overview ? `${formatNumber(overview.interactions.messages)} chat · ${formatNumber(overview.interactions.examConsultants)} exames · ${formatNumber(overview.interactions.signsSymptomsAnalyses)} sinais · ${formatNumber(overview.interactions.examAnalyses)} análises` : 'Chat + exames + sinais + análises'}
            icon={<Activity className="h-6 w-6" />}
            color="accent"
            isLoading={isLoading}
          />
          <MetricCard
            title="Tempo total de uso"
            value={overview ? formatDuration(overview.estimatedActiveMinutes) : '—'}
            description="Estimado a partir da atividade dos usuários"
            icon={<Clock className="h-6 w-6" />}
            color="coentro"
            isLoading={isLoading}
          />
          <MetricCard
            title="Nível geral de utilização"
            value={overview ? `${overview.usageLevelPct}%` : '—'}
            description="Ativos sobre total de clientes"
            icon={<Gauge className="h-6 w-6" />}
            color="primary"
            isLoading={isLoading}
          />
          <MetricCard
            title="Consumo de créditos"
            value={overview ? formatBRL(overview.credits.consumedInMonth) : '—'}
            description="Gasto pelos usuários no período"
            icon={<Coins className="h-6 w-6" />}
            color="destructive"
            isLoading={isLoading}
          />
          <MetricCard
            title="Créditos disponíveis"
            value={overview ? formatBRL(overview.credits.availableNow) : '—'}
            description="Saldo total dos clientes (agora)"
            icon={<Wallet className="h-6 w-6" />}
            color="accent"
            isLoading={isLoading}
          />
          <MetricCard
            title="Créditos comprados"
            value={overview ? formatBRL(overview.credits.purchasedInMonth) : '—'}
            description="Compras pagas no período"
            icon={<ShoppingBag className="h-6 w-6" />}
            color="coentro"
            onReload={() => _fetchOverview(true)}
            isLoading={isLoading}
          />
        </div>
      </section>

      <div className="grid grid-cols-1 smAndUp:grid-cols-2 mdAndUp:grid-cols-3 lgAndUp:grid-cols-4 gap-4 mb-4">
        <MetricCard title="API Requisições" value={formatNumber(totalRequests)} icon={<ZapIcon className="h-6 w-6" />} color="purple" isLoading={isLoading} />
        <MetricCard title="Custo Estimado" value={`R$ ${estimatedCost.toFixed(2)}`} icon={<DollarSign className="h-6 w-6" />} color="green" isLoading={isLoading} />
      </div>

      <div className="grid grid-cols-1 gap-6 mb-4">
        <PurchasesChart data={purchases} />
      </div>

      <div className="grid grid-cols-1 gap-6 mb-4">
        <RequestsChart data={chronologicalData} />
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6 transition-all duration-200 hover:shadow-md mb-4">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Detalhes de Uso</h2>
        <UsageTable data={chronologicalData} />
      </div>
    </>
  )
}
